import { PhoneCallIcon } from '@phosphor-icons/react'
import { Handle, type NodeProps, Position, useNodeConnections, useNodeId } from '@xyflow/react'
import { cn } from 'app/lib/utils'
import type { WaitForCallResultFlowNode } from 'app/types/workflows'
import { AppendButton } from './AppendButton'
import { NodeShell } from './NodeShell'

const HANDLE = `!h-2.5 !w-2.5 !border-2 !border-(--surface)`

// Fixed call dispositions, each with its own output. `left` positions the handle + append button.
const OUTCOMES = [
  { id: `answered`, label: `ANSWERED`, left: `18%`, dark: true },
  { id: `no_answer`, label: `NO ANSWER`, left: `50%`, dark: false },
  { id: `voicemail`, label: `VOICEMAIL`, left: `82%`, dark: false },
] as const

/** Forks on the outcome of an upstream Initiate call. Coupled — invalid without a call to react to
 *  (enforced via the shared issues map; see NodeShell). */
export function WaitForCallResultNode({ data, selected }: NodeProps<WaitForCallResultFlowNode>) {
  const nodeId = useNodeId()

  return (
    <NodeShell
      icon={PhoneCallIcon}
      accent="violet"
      kind="Wait for call result"
      label={data.label}
      description="Fork on how the call went"
      selected={selected}
      hasSource={false}
    >
      <div className="flex justify-between px-4 pb-2 text-[9px] font-semibold">
        {OUTCOMES.map((outcome) => (
          <span key={outcome.id} className={outcome.dark ? `text-default` : `text-minimal`}>
            {outcome.label}
          </span>
        ))}
      </div>
      {OUTCOMES.map((outcome) => (
        <Handle
          key={outcome.id}
          id={outcome.id}
          type="source"
          position={Position.Bottom}
          style={{ left: outcome.left }}
          className={cn(HANDLE, outcome.dark ? `!bg-inverse` : `!bg-inverse/40`)}
        />
      ))}
      {nodeId != null &&
        OUTCOMES.map((outcome) => (
          <OutcomeAppend key={outcome.id} nodeId={nodeId} handle={outcome.id} left={outcome.left} />
        ))}
    </NodeShell>
  )
}

/** Append "+" for one outcome handle, shown only while that handle is unconnected. */
function OutcomeAppend({ nodeId, handle, left }: { nodeId: string; handle: string; left: string }) {
  const open = useNodeConnections({ handleType: `source`, handleId: handle }).length === 0
  if (!open) return null
  return <AppendButton nodeId={nodeId} sourceHandle={handle} className="-bottom-8 -translate-x-1/2" style={{ left }} />
}
